import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { FlatButton, TransparentFlatButton, CloseButton } from '@ui/buttons';
import { RowChatContext } from './RowChatContext';
import { useRowChat } from './useRowChat';
import { RowChatPanelMessage } from './RowChatPanelMessage';

const PANEL_WIDTH = 600;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 999;
`;

const PanelWrapper = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${PANEL_WIDTH}px;
  z-index: 1000;
  background: ${({ theme }) => theme.themeColors.appBackground};
  border-left: 1px solid ${({ theme }) => theme.themeColors.border};
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  gap: 12px;
`;

const HeaderQuestion = styled.div`
  flex: 1;
  min-width: 0;
  padding-top: 5px;
`;

const CloseButtonWrapper = styled.div`
  cursor: pointer;
  flex-shrink: 0;
  padding-top: 2px;

  svg {
    height: 14px;
    width: 14px;
  }

  g {
    fill: ${({ theme }) => theme.themeColors.text};
  }
`;

const ThreadWrapper = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InputSection = styled.div`
  padding: 12px 20px;
  border-top: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const InputRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
`;

const StyledTextarea = styled.textarea`
  flex: 1;
  resize: vertical;
  min-height: 48px;
  max-height: 120px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 6px;
  background: #ffffff;
  color: ${({ theme }) => theme.themeColors.text};
  font-family: inherit;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.themeColors.primaryCtaButton};
  }
`;

const ActionsRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 8px;
`;

const SubmitButtonWrapper = styled.div`
  flex-shrink: 0;

  button {
    padding: 10px 20px;
  }
`;

const ScrollAnchor = () => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  });
  return <div ref={ref} />;
};

export const RowChatPanel = () => {
  const { activeRowIndex, activeRow, messages, isStreaming, error, closeChat, replaceRow } =
    React.useContext(RowChatContext);
  const { sendMessage } = useRowChat();
  const [inputValue, setInputValue] = React.useState('');

  if (activeRowIndex === null) return null;

  const handleSubmit = () => {
    const text = inputValue.trim();
    if (!text || isStreaming) return;
    setInputValue('');
    sendMessage(text);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const hasNonOriginalAssistant = messages.some((m) => m.role === 'assistant' && !m.isOriginal);

  return (
    <>
      <Overlay onClick={closeChat} />
      <PanelWrapper>
        <PanelHeader>
          <HeaderQuestion>
            <Body5 bold>Chat</Body5>
            {activeRow && (
              <Container top={4}>
                <Body5>{activeRow.question}</Body5>
              </Container>
            )}
          </HeaderQuestion>
          <CloseButtonWrapper>
            <CloseButton onClick={closeChat} />
          </CloseButtonWrapper>
        </PanelHeader>

        <ThreadWrapper>
          {messages.map((msg, idx) => (
            <RowChatPanelMessage key={idx} message={msg} isStreaming={isStreaming && idx === messages.length - 1} />
          ))}
          {error && <Body5 color="red">{error}</Body5>}
          <ScrollAnchor />
        </ThreadWrapper>

        <InputSection>
          <InputRow>
            <StyledTextarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a follow-up..."
              disabled={isStreaming}
            />
            <SubmitButtonWrapper>
              <FlatButton onClick={handleSubmit} disabled={isStreaming || !inputValue.trim()}>
                Send
              </FlatButton>
            </SubmitButtonWrapper>
          </InputRow>
          <ActionsRow>
            <TransparentFlatButton onClick={replaceRow} disabled={isStreaming || !hasNonOriginalAssistant}>
              Replace Original Answer With Latest Chat Answer
            </TransparentFlatButton>
          </ActionsRow>
        </InputSection>
      </PanelWrapper>
    </>
  );
};
